import React, { useCallback, useEffect, useMemo, useState } from 'react';
import shallow from 'zustand/shallow';
import './LeftSide.less';
import OverlaySider from '../Layout/OverlaySider';
import { navbarHeight, leftSidebarContentWidth, leftRailWidth } from '../../constants/gridConfig';
import SummarySide from './SummarySide';
import SectionTree from './SectionTree';
import ExportPanel from './ExportPanel';
import DocumentsPanel from './DocumentsPanel';
import ElementsPanel from './ElementsPanel';
import LayoutsPanel from './LayoutsPanel';
import SettingsPanel from './SettingsPanel';
import LeftSideRail from './LeftSideRail';
import { useStore } from '../../store/store';
import { pageBackground, PRIMARY_DARK, PANEL_LIGHT_BG } from '../../constants/constants';
import VariableHandler from '../VariableHandler/VariableHandler';
import FormItemsArray from '../VariableHandler/FormCreator/FormItemsArray';
import { useOverlay, leftShownStatus } from '../../contexts/OverlayContext';
import { getVariablesGroupedByContainer } from '../../utils/variables';
import LeftSideButton from './LeftSideButton';
import RecursiveSectionSettings from './RecursiveSectionSettings';
import { useModalOpener, useWindowDimensions } from '../../hooks';
import { Switch, Tabs, message } from 'antd';
import TemplateModal from '../RightSide/TemplateModal';
import NewTemplateModal from '../RightSide/NewTemplateModal';
import { withoutStructureSync } from '../../collab/structureSync';
import { flushPendingSaves } from '../../collab/flushPendingSaves';
import { isWindows } from '../../utils/browser';

// Clés pour les panels du Collapse : les deux ouverts par défaut, chaque
// onglet du rail (Sommaire/Sections) n'affiche plus que l'un des deux à la
// fois donc plus besoin de les faire s'exclure l'un l'autre.
const SUMMARY_PANEL_KEY = 'summary';
const LINKED_SECTIONS_PANEL_KEY = 'linked-sections';

const LeftSide = () => {
  // Récupérer les dépendances nécessaires depuis le store. `shallow` : sans
  // lui, l'objet forgé à chaque appel redessinait tout le panel gauche (et la
  // modale Documents qu'il monte) à chaque changement du store.
  const { variableGroups, variables, boxes, masks, containers, columns, rowContainers } = useStore(({
    variableGroups,
    variables,
    boxes,
    masks,
    containers,
    columns,
    rowContainers
  }) => ({
    variableGroups,
    variables,
    boxes,
    masks,
    containers,
    columns,
    rowContainers
  }), shallow)

  // Mémoriser le calcul coûteux de getVariablesGroupedByContainer
  const variablesGroupedByContainer = useMemo(() => {
    return getVariablesGroupedByContainer({
      variableGroups,
      variables,
      boxes: [
        ...boxes,
        ...masks.map((m) => m.boxes).flat()
      ],
      containers,
      columns,
      rowContainers,
    })
  }, [variableGroups, variables, boxes, masks, containers, columns, rowContainers])

  const { width } = useWindowDimensions()
  const [columnToShow, setColumnToShow] = useState('triggers')
  const [hidePreview, setHidePreview] = useState(false)
  const isTemplate = useStore(({ currentDocument }) => currentDocument?.type === 'template')

  // Modal Documents (TemplateModal/NewTemplateModal) : montée ici (pas dans
  // l'onglet "Documents" du rail, cf. DocumentsPanel) pour rester active quel
  // que soit l'onglet affiché, notamment pour l'ouverture automatique sur un
  // nouveau template ci-dessous.
  const currentDocument = useStore(({ currentDocument }) => currentDocument)
  const currentProposal = useStore(({ currentProposal }) => currentProposal)
  const loadingData = useStore(({ loadingData }) => loadingData)
  const templates = useStore(({ templates }) => templates)
  const isAdmin = useStore(({ user }) => user?.isAdmin)
  const postTemplate = useStore(({ postTemplate }) => postTemplate)
  const createTemplateInStore = useStore(({ createTemplateInStore }) => createTemplateInStore)

  const [handleTemplateOpen, setHandleTemplateOpen] = useModalOpener(false)
  const [newTemplateModalVisible, setNewTemplateModalVisible] = useModalOpener(false)
  const [newTemplateDefaultRecursive, setNewTemplateDefaultRecursive] = useState(false)

  useEffect(() => {
    if (!currentProposal?.id) {
      if (currentDocument.type === 'template' && !currentDocument.id) {
        setHandleTemplateOpen(true);
      }
    }
  }, [currentProposal, currentDocument, setHandleTemplateOpen])

  const saveNewTemplate = useCallback(
    async ({ name, templatable, templatePattern, landscape, work, documentTypeIds, isRecursive, recursiveModelName, recursiveAgreementType, folderId, adminOnly }) => {
      const key = "saveNewTemplate";
      message.open({
        type: 'loading',
        content: 'Création du document en cours',
        duration: 0,
        key
      })
      // Le store va être vidé pour le nouveau modèle : ce qui n'est pas encore
      // enregistré sur le document ouvert serait perdu. On n'y va qu'une fois
      // tout enregistré.
      if (!(await flushPendingSaves())) {
        message.open({
          type: 'error',
          content:
            "Création impossible : des modifications du document ouvert ne sont pas encore enregistrées. Réessayez dès qu'elles le seront (bouton « Enregistrer » en haut de la page).",
          duration: 6,
          key,
        });
        return;
      }
      withoutStructureSync(createTemplateInStore);
      if (isAdmin && currentDocument.type === 'template') {
        try {
          await postTemplate({
            name,
            templatable,
            templatePattern,
            landscape,
            work,
            documentTypeIds,
            isRecursive,
            recursiveModelName,
            recursiveAgreementType,
            folderId,
            adminOnly,
          });
          message.open({
            type: 'success',
            content: 'Document créé.',
            duration: 2,
            key
          })
        } catch (error) {
          message.open({
            type: 'error',
            content: "Une erreur est survenue lors de la création.",
            duration: 4,
            key
          })
        }
      }
      setNewTemplateModalVisible(false);
      setHandleTemplateOpen(false);
    },
    [createTemplateInStore, isAdmin, currentDocument.type, setNewTemplateModalVisible, setHandleTemplateOpen, postTemplate]
  );

  const handleCreateTemplate = (isRecursive = false) => {
    setNewTemplateDefaultRecursive(isRecursive);
    setNewTemplateModalVisible(true);
  };

  const salesmanChoseTemplate =
    !isAdmin &&
    (currentDocument.type === 'offer' ||
      currentDocument.type === 'opportunity');

  const canSave = isAdmin || salesmanChoseTemplate;

  // État pour gérer les panels ouverts
  const [activePanels, setActivePanels] = useState([SUMMARY_PANEL_KEY, LINKED_SECTIONS_PANEL_KEY])
  const [selectedSummaryTitle, setSelectedSummaryTitle] = useState(null)

  // Gérer le changement d'état des panels
  const handlePanelChange = (keys) => {
    const keysArray = Array.isArray(keys) ? keys : [keys]
    setActivePanels(keysArray)
  }


  const { overlayProps } = useOverlay();

  const shownStatus = overlayProps.left.shownStatus

  useEffect(() => {
    if (shownStatus === leftShownStatus.VARIABLES) setColumnToShow('triggers')
  }, [shownStatus])

  // Mode "formulaire plein écran" (FormCreator, tableau drag multi-colonnes) :
  // uniquement en template. En proposal, le formulaire tient dans le panel
  // standard (une seule colonne, cf. FormItemsArray) donc pas besoin du mode
  // pleine largeur.
  const onlyButton = shownStatus === leftShownStatus.VARIABLES && isTemplate

  // Redimensionnement du panel pour les onglets Sommaire/Sections (arbres à
  // titres longs) et Formulaire en proposal (noms de variables longs). Le
  // panel est un overlay (position:fixed, cf. OverlaySider) : l'élargir
  // recouvre le canvas, aucun offset à recalculer. Largeur mini = largeur
  // fixe actuelle, maxi bornée.
  const [panelWidth, setPanelWidth] = useState(leftSidebarContentWidth);
  const resizable =
    !onlyButton &&
    (shownStatus === leftShownStatus.SUMMARY ||
      shownStatus === leftShownStatus.SECTIONS ||
      (shownStatus === leftShownStatus.VARIABLES && !isTemplate));
  const contentWidth = resizable ? panelWidth : leftSidebarContentWidth;

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMove = (ev) => {
      const next = Math.min(640, Math.max(leftSidebarContentWidth, startWidth + (ev.clientX - startX)));
      setPanelWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  const items = [
    {
      key: 'triggers',
      label: 'Déclencheurs',
    },
    {
      key: 'preview',
      label: 'Aperçu du formulaire',
    },
  ];
  return (
    <>
      {!loadingData && canSave && (
        <TemplateModal
          open={handleTemplateOpen}
          handleCancel={() => setHandleTemplateOpen(false)}
          handleOk={() => {
            setNewTemplateModalVisible(false);
            setHandleTemplateOpen(false);
          }}
          data={templates}
          isAdmin={isAdmin}
          newTemplateOnClick={handleCreateTemplate}
        />
      )}
      {isAdmin && !loadingData && newTemplateModalVisible && (
        <NewTemplateModal
          type={isAdmin ? 'template' : 'proposition'}
          open={true}
          handleCancel={() => setNewTemplateModalVisible(false)}
          handleOk={saveNewTemplate}
          defaultIsRecursive={newTemplateDefaultRecursive}
        />
      )}
      {/*
        Rail en colonne fixe indépendante, toujours pleine hauteur. En mode
        formulaire (onlyButton), OverlaySider ci-dessous redevient un bandeau
        plein-écran mais à hauteur "auto" (juste assez pour son header) : si
        le rail restait à l'intérieur de ce bandeau, sa hauteur (6 boutons)
        forcerait celle du bandeau à grandir d'autant sur toute la largeur de
        l'écran, cachant le formulaire dessous. Zindex 19 : au-dessus
        d'OverlaySider (18) et de VariableHandler (16).
      */}
      <div style={{
        position: 'fixed',
        top: navbarHeight,
        left: 0,
        height: `calc(100vh - ${navbarHeight}px)`,
        backgroundColor: PANEL_LIGHT_BG,
        zIndex: 19,
      }}>
        <LeftSideRail variablesGroupedByContainer={variablesGroupedByContainer} />
      </div>
      <OverlaySider
        side='left'
        zIndex={18}
        onlyButton={onlyButton}
        width={onlyButton ? "100%" : contentWidth + leftRailWidth}
        fontSize={9}
      >
        <div style={{
          width: "100%",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: leftRailWidth,
          paddingRight: onlyButton ? 16 : undefined
        }}>
          <div
            className={isWindows() ? 'left-side__summary windows-scrollbar' : 'left-side__summary'}
            style={{
              height: onlyButton ? undefined : `calc(100vh - ${navbarHeight}px)`,
              overflowY: 'scroll',
              overflowX: onlyButton ? 'visible' : 'hidden',
              width: onlyButton ? "max-content" : contentWidth,
              minWidth: onlyButton ? 620 : undefined,
            }}
            data-role={pageBackground}
          >
            <LeftSideButton variablesGroupedByContainer={variablesGroupedByContainer} />
            {isTemplate && shownStatus === leftShownStatus.SUMMARY && <RecursiveSectionSettings />}
            {shownStatus === leftShownStatus.SUMMARY &&
              <div className='left-side__wrapper'>
                <SummarySide
                  selectedSummaryTitle={selectedSummaryTitle}
                  onSummaryTitleSelect={setSelectedSummaryTitle}
                />
              </div>
            }
            {shownStatus === leftShownStatus.SECTIONS &&
              <div className='left-side__wrapper'>
                <SectionTree />
              </div>
            }
            {shownStatus === leftShownStatus.EXPORT &&
              <div className='left-side__wrapper'>
                <ExportPanel />
              </div>
            }
            {shownStatus === leftShownStatus.DOCUMENTS &&
              <div className='left-side__wrapper'>
                <DocumentsPanel
                  onOpenDocuments={() => setHandleTemplateOpen(true)}
                  onCreateTemplate={handleCreateTemplate}
                  isAdmin={isAdmin}
                />
              </div>
            }
            {shownStatus === leftShownStatus.ELEMENTS &&
              <div className='left-side__wrapper'>
                <ElementsPanel />
              </div>
            }
            {shownStatus === leftShownStatus.LAYOUTS &&
              <div className='left-side__wrapper'>
                <LayoutsPanel />
              </div>
            }
            {shownStatus === leftShownStatus.SETTINGS &&
              <div className='left-side__wrapper'>
                <SettingsPanel />
              </div>
            }
            {shownStatus === leftShownStatus.VARIABLES && !isTemplate &&
              // marginTop : le bloc LeftSideButton (switch + recharger) est en
              // position sticky juste au-dessus, sans lui-même de marge basse ;
              // sans ce décalage le tableau colle dessous.
              <div className='left-side__wrapper' style={{ marginTop: 12 }}>
                <FormItemsArray variablesGroupedByContainer={variablesGroupedByContainer} />
              </div>
            }
          </div>
          {onlyButton && isTemplate && width < 1860 && <div>
            <Tabs
              className="left-side-form-tabs"
              onChange={(key) => setColumnToShow(key)}
              defaultActiveKey="triggers"
              items={items}
            />
          </div>}
          {onlyButton && isTemplate && width >= 1860 && <div style={{
            color: PRIMARY_DARK,
            display: 'flex',
            gap: 8,
            alignItems: 'center'
          }}>
            Cacher l'aperçu du formulaire :
            <Switch onChange={(checked) => setHidePreview(checked)} />
          </div>}
        </div>
        {resizable && (
          // Poignée de redimensionnement collée au bord droit du panel (overlay
          // fixed) : glisser pour élargir, borné [largeur mini, 640px].
          <div
            onMouseDown={startResize}
            title="Redimensionner"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 6,
              height: '100%',
              cursor: 'col-resize',
              zIndex: 20,
            }}
          />
        )}
      </OverlaySider >
      {shownStatus === leftShownStatus.VARIABLES && isTemplate &&
        <VariableHandler hidePreview={hidePreview} columnToShow={columnToShow} variablesGroupedByContainer={variablesGroupedByContainer} />
      }
    </>
  );
};

export default React.memo(LeftSide);
